import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, BellOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const toKey = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const RING_SECONDS = 6;
export const VIBRATE_6S = [500, 200, 500, 200, 500, 200, 500, 200, 500, 200, 500, 200, 500, 200, 500, 200];

let audioCtx = null;
let lastRing = 0;
export function ringAlert() {
  if (Date.now() - lastRing < RING_SECONDS * 1000) return;
  lastRing = Date.now();
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    const start = audioCtx.currentTime + 0.05;
    for (let t = 0; t < RING_SECONDS; t += 0.25) {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.type = "square";
      o.frequency.value = Math.round(t * 4) % 2 ? 1320 : 990;
      g.gain.setValueAtTime(1, start + t);
      g.gain.setValueAtTime(0, start + t + 0.2);
      o.connect(g).connect(audioCtx.destination);
      o.start(start + t);
      o.stop(start + t + 0.21);
    }
  } catch {
    /* audio unavailable */
  }
  if (navigator.vibrate) navigator.vibrate(VIBRATE_6S);
}

export const RideAlerts = () => {
  const [state, setState] = useState("checking");
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMsg = (e) => {
      if (e.data?.type === "ride-ring") ringAlert();
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, []);
  useEffect(() => {
    if (!supported()) {
      setState("unsupported");
      return;
    }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (sub) api.post("/push/subscribe", sub.toJSON()).catch(() => {});
        setState(sub && Notification.permission === "granted" ? "on" : Notification.permission === "denied" ? "denied" : "off");
      })
      .catch(() => setState("off"));
  }, []);

  const enable = async () => {
    ringAlert();
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState("denied");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.key) }));
      await api.post("/push/subscribe", sub.toJSON());
      setState("on");
      await api.post("/push/test");
      toast.success("Ride alerts are on — a test alert was sent");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const disable = async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
      await sub.unsubscribe();
    }
    setState("off");
  };

  if (state === "checking") return null;
  const on = state === "on";
  return (
    <div data-testid="ride-alerts-card" className={`flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center ${on ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
      {on ? <BellRing className="h-6 w-6 shrink-0 text-emerald-600" /> : <BellOff className="h-6 w-6 shrink-0 text-amber-600" />}
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold text-slate-900" data-testid="ride-alerts-status">{on ? "Ride alerts are on for this phone" : "Turn on ride alerts"}</p>
        <p className="text-slate-600">
          {state === "unsupported"
            ? isIOS() && !standalone()
              ? "On iPhone, first install the app (Share → Add to Home Screen), then open it from your home screen to turn on alerts."
              : "This browser can't show alerts when minimised. Use Chrome, Edge or Samsung Internet."
            : state === "denied"
              ? "Notifications are blocked. Allow notifications for this site in your browser settings, then try again."
              : on
                ? "Your phone will ring and vibrate for new ride offers, even when the app is minimised."
                : "Get a ping + vibration for every ride offer, even when the app is minimised or the screen is off."}
        </p>
      </div>
      {state === "off" && <button data-testid="enable-ride-alerts-button" onClick={enable} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600">Turn on alerts</button>}
      {on && (
        <div className="flex gap-2">
          <button data-testid="test-ride-alert-button" onClick={() => api.post("/push/test").then(() => toast.success("Test alert sent")).catch((e) => toast.error(errMsg(e)))} className="rounded-xl border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-700">Send test</button>
          <button data-testid="disable-ride-alerts-button" onClick={disable} className="rounded-xl px-3 py-2 text-xs font-semibold text-slate-500 hover:underline">Turn off</button>
        </div>
      )}
    </div>
  );
};
